import {
  computed,
  Inject,
  Injectable,
  InjectionToken,
  Optional,
  Provider,
  Signal,
  signal,
  ValueEqualityFn
} from '@angular/core';

const SIGNAL_STORE_ID = new InjectionToken('SIGNAL_STORE_ID');
const SIGNAL_STORE_CONFIG = new InjectionToken('SIGNAL_STORE_CONFIG');

export interface SignalStoreConfig<T> {
  initState?: Partial<T>;
  equal?: ValueEqualityFn<T>
}

export function InitializeStore<T>(
  storeId: string,
  config?: Partial<SignalStoreConfig<T>>
): Provider[] {
  return [
    {
      provide: storeId,
      useFactory: () => new SignalStore<T>(storeId, config as SignalStoreConfig<T>)
    }
  ]
}

@Injectable()
export class SignalStore<T> {
  readonly state = signal<T>(this.config?.initState as T);

  constructor(
    @Optional() @Inject(SIGNAL_STORE_ID) private readonly storeId?: string,
    @Optional() @Inject(SIGNAL_STORE_CONFIG) private config?: SignalStoreConfig<T>
  ) {
    this.config = Object.assign(this.config ?? {}, {storeId: this.storeId});
  }

  set(state: T) {
    this.state.set(state);
  }

  setKey<K extends keyof T>(key: K, value: any) {
    this.state.update(state => {
      state[key] = value;
      return {...state};
    });
  }

  update(updater: (state: T) => T) {
    this.state.update(updater);
  }

  reset() {
    this.state.set(this.config?.initState as T);
  }

  select<U>(selector: (state: T) => U) {
    return computed(() => {
      const state = this.state();
      return selector(state)
    })
  }

  selectKey<K extends keyof T>(key: K): Signal<T[K]> {
    return computed(() => this.state()[key])
  }

  selectFrom<T, U>(signal: Signal<T>, selector: (state: T) => U) {
    return computed(() => selector(signal()));
  }
}
